// Simple light/dark theme manager. Adds/removes `dark` on <html> and Element
// Plus's expected `html.dark` class, persisting the choice in localStorage.
const STORAGE_KEY = "theme";

export function getStoredTheme() {
  return localStorage.getItem(STORAGE_KEY); // 'light' | 'dark' | null
}

export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

export function setTheme(theme) {
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}

// Toggle with a single smooth, app-wide cross-fade via the View Transitions
// API; falls back to an instant swap where the API is unavailable.
export function toggleTheme() {
  const next = isDark() ? "light" : "dark";

  const apply = () => {
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
  };

  const supportsVT =
    typeof document !== "undefined" &&
    typeof document.startViewTransition === "function";
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (!supportsVT || reduceMotion) {
    apply();
    return next;
  }

  // One coordinated cross-fade of the whole screen: the new snapshot fades in
  // over the old one. No hard clip-path edge, so nothing "wipes" or flickers.
  const transition = document.startViewTransition(apply);

  transition.ready
    .then(() => {
      document.documentElement.animate(
        { opacity: [0, 1] },
        {
          duration: 380,
          easing: "ease",
          fill: "forwards",
          pseudoElement: "::view-transition-new(root)",
        }
      );
    })
    .catch(() => {});

  return next;
}

export function isDark() {
  return document.documentElement.classList.contains("dark");
}

// Called once at startup: stored choice wins, otherwise default to light.
export function initTheme() {
  const stored = getStoredTheme();
  applyTheme(stored === "dark" ? "dark" : "light");
}
